import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { animeList, genreOptions } from "@/mocks/anime";

const stats = [
  { label: "收录作品", value: `${animeList.length}+`, icon: "ri-film-line" },
  { label: "动漫类型", value: `${genreOptions.length}+`, icon: "ri-price-tag-3-line" },
  { label: "覆盖地区", value: "3", icon: "ri-earth-line" },
  { label: "每日更新", value: "持续", icon: "ri-refresh-line" },
];

const scopeItems = [
  {
    icon: "ri-fire-line",
    title: "最新热门动画",
    desc: "紧跟每季新番，收录正在连载的热门动画与话题作品，保持在追番的第一线。",
  },
  {
    icon: "ri-archive-2-line",
    title: "经典动漫作品",
    desc: "整理历年口碑经典与高人气老番，方便新老观众回顾与补番。",
  },
  {
    icon: "ri-layout-grid-line",
    title: "多维度分类",
    desc: "按类型、地区、年份细化分类，配合热度与评分排序，快速定位想看的内容。",
  },
  {
    icon: "ri-bookmark-3-line",
    title: "收藏与追番",
    desc: "一键收藏喜欢的作品，保存在本地浏览器，随时回到你的追番清单。",
  },
];

const guides = [
  { step: "01", title: "浏览首页", desc: "通过焦点轮播与最新推荐，快速发现当前热门动漫。" },
  { step: "02", title: "进入分类筛选", desc: "在分类页按类型、地区、年份多选组合，精准缩小范围。" },
  { step: "03", title: "查看详情", desc: "点开作品查看剧情简介、作品信息与剧集列表。" },
  { step: "04", title: "收藏感兴趣的作品", desc: "点击收藏，下次访问即可快速找到。" },
];

const contacts = [
  { icon: "ri-mail-line", label: "合作与反馈邮箱", value: "hello@sakura-anime.example.com" },
  { icon: "ri-wechat-line", label: "官方公众号", value: "樱花动漫SAKURA" },
  { icon: "ri-weibo-line", label: "官方微博", value: "@樱花动漫网站" },
];

export default function About() {
  useSeo({
    title: "关于我们 - 樱花动漫网站",
    description:
      "了解樱花动漫网站的品牌故事、平台定位与内容范围。我们专注于热门动漫资源的在线观看与信息展示，提供收藏、分类筛选等便捷追番体验。",
    keywords: "关于樱花动漫网站,樱花动漫介绍,动漫平台,动漫在线观看,追番",
  });

  return (
    <div className="mx-auto px-4 py-6 md:px-8 md:py-10">
      <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-700">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-900">关于我们</span>
      </nav>

      <section className="mt-4 overflow-hidden rounded-lg bg-primary-500 p-6 md:p-12">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/20 px-3 py-1 text-xs font-semibold text-background-50">
            <i className="ri-flower-fill" />
            关于樱花动漫网站
          </span>
          <h1 className="mt-4 font-heading text-3xl font-extrabold text-background-50 md:text-4xl">
            让每一次追番都简单一点
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-background-50/90 md:text-base">
            樱花动漫网站是专注于热门动漫资源在线观看与信息展示的动漫门户。我们相信，找到一部喜欢的动漫不该是件麻烦事。
            因此我们用心整理每一部作品的信息，用清晰的分类与筛选，帮你在海量动画中快速遇见对的那一部。
          </p>
        </div>
      </section>

      <section className="mt-10" aria-label="平台数据">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5">
          {stats.map((item) => (
            <div
              key={item.label}
              className="rounded-lg border border-background-200/70 bg-background-50 p-4 md:p-5"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-lg text-accent-700">
                <i className={item.icon} />
              </span>
              <p className="mt-3 font-heading text-2xl font-extrabold text-foreground-950">
                {item.value}
              </p>
              <p className="mt-0.5 text-sm text-foreground-500">{item.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 md:mt-14">
        <h2 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          网站定位与内容范围
        </h2>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
          我们面向喜欢动漫、习惯追番的动漫爱好者，提供从发现、了解到了解作品信息的完整体验。内容覆盖以下几个方面：
        </p>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {scopeItems.map((item) => (
            <div
              key={item.title}
              className="flex gap-4 rounded-lg border border-background-200/70 bg-background-50 p-5"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-xl text-primary-700">
                <i className={item.icon} />
              </span>
              <div>
                <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 md:mt-14">
        <h2 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          使用说明
        </h2>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
          只需四步，就能在樱花动漫网站找到并收藏你喜欢的作品。
        </p>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {guides.map((guide) => (
            <div
              key={guide.step}
              className="rounded-lg border border-background-200/70 bg-background-50 p-5"
            >
              <span className="font-heading text-2xl font-extrabold text-primary-300">
                {guide.step}
              </span>
              <h3 className="mt-2 font-heading text-base font-bold text-foreground-950">
                {guide.title}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{guide.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 md:mt-14">
        <h2 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          联系方式
        </h2>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
          如果你对网站有任何建议、合作意向或内容反馈，欢迎通过以下方式与我们取得联系。
        </p>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {contacts.map((item) => (
            <div
              key={item.label}
              className="rounded-lg border border-background-200/70 bg-secondary-50 p-5"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-lg text-secondary-700">
                <i className={item.icon} />
              </span>
              <p className="mt-3 text-xs text-foreground-500">{item.label}</p>
              <p className="mt-1 break-all text-sm font-semibold text-foreground-900">
                {item.value}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-col gap-4 rounded-lg border border-background-200/70 bg-background-50 p-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-foreground-600">
            想直接开始找番？进入分类页或使用搜索，马上开始你的追番之旅。
          </p>
          <div className="flex shrink-0 flex-wrap gap-3">
            <Link
              to="/category"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-layout-grid-line" />
              浏览分类
            </Link>
            <Link
              to="/search"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-200 bg-background-50 px-5 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"
            >
              <i className="ri-search-line" />
              搜索动漫
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}